import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';

const checkElemOverflow = (elem: Element): boolean => {
  return elem.scrollWidth > elem.clientWidth;
};

const checkChildrenOverflow = (elem: Element, depth: number): boolean => {
  for (const child of elem.children) {
    if (checkElemOverflow(child)) {
      return true;
    } else if (depth > 0) {
      if (checkChildrenOverflow(child, depth - 1)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Hook to detect overflows, i.e. text that does not fit given HTMLElement
 * @param ref Reference to HTMLElement to check overflow on
 * @param valueOrRenderer Used only for value change detection,
 * used only in deps array of useEffect
 * @param depth depth 0 = check just given element;
 * depth 1 = check given element and its direct (1st generation) children, etc
 */
export const useIsOverflowing = (ref: React.RefObject<HTMLElement | null>, valueOrRenderer: string | (() => void) | unknown, depth: number = 0) => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const elem = ref.current;

    if (elem) {
      const isElemOverflowing = checkElemOverflow(elem);
      if (isElemOverflowing) {
        setIsOverflowing(true);
      } else if (depth > 0) {
        const isChildrenOverflowing = checkChildrenOverflow(elem, depth - 1);

        if (isChildrenOverflowing) {
          setIsOverflowing(true);
        } else {
          setIsOverflowing(false);
        }
      } else {
        setIsOverflowing(false);
      }
    } else {
      setIsOverflowing(false);
    }
  }, [depth, ref]);
  useEffect(() => {
    checkOverflow();
  }, [checkOverflow, valueOrRenderer]);
  useEffect(() => {
    const debouncedCheckOverflow = debounce(checkOverflow, 200);
    window.addEventListener('resize', debouncedCheckOverflow);
    return () => {
      debouncedCheckOverflow.cancel();
      window.removeEventListener('resize', debouncedCheckOverflow);
    };
  }, [checkOverflow]);

  return { isOverflowing };
};
